/**
 * AuthGuard component - Protects routes requiring authentication
 * Updated for React Router 7
 */

import * as React from "react"
import { Outlet, Navigate, useLocation } from "react-router"
import { useAuth } from "@/hooks/use-auth"

interface AuthGuardProps {
  children?: React.ReactNode
  requiredRoles?: string[]
  fallback?: React.ReactNode
}

/**
 * AuthGuard component for React Router
 * Use as a route element to protect child routes
 * 
 * Usage in routes:
 * <Route element={<AuthGuard />}>
 *   <Route path="/dashboard" element={<Dashboard />} />
 * </Route>
 */
export function AuthGuard({
  children,
  requiredRoles,
  fallback,
}: AuthGuardProps) {
  const { user, isLoading, isAuthenticated } = useAuth()
  const location = useLocation()
  const [isCheckingAuth, setIsCheckingAuth] = React.useState(true)

  React.useEffect(() => {
    // Check localStorage directly to avoid race conditions
    const checkAuth = () => {
      const token = localStorage.getItem("auth_token")
      const storedUser = localStorage.getItem("auth_user")
      
      if (!token || !storedUser) {
        // Store current path for redirect after login
        sessionStorage.setItem("redirect_after_login", location.pathname)
        setIsCheckingAuth(false)
      } else {
        setIsCheckingAuth(false)
      }
    }

    // Small delay to ensure localStorage is accessible
    const timer = setTimeout(checkAuth, 50)
    return () => clearTimeout(timer)
  }, [location.pathname])

  // Show loading state while checking auth
  if (isCheckingAuth || isLoading) {
    return (
      fallback || (
        <div className="flex min-h-screen items-center justify-center bg-gray-50">
          <div className="flex flex-col items-center gap-4">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-primary border-t-transparent" />
            <p className="text-sm text-gray-500">Loading...</p>
          </div>
        </div>
      )
    )
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  // Check role requirements
  if (requiredRoles && requiredRoles.length > 0) {
    const userRoles = user?.roles || []
    const hasRequiredRole = requiredRoles.some((role) =>
      userRoles.includes(role)
    )

    if (!hasRequiredRole) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-red-600">
              Access Denied
            </h1>
            <p className="mt-2 text-gray-600">
              You don't have permission to access this page.
            </p>
            <button
              onClick={() => window.history.back()}
              className="mt-4 text-brand-primary underline"
            >
              Go back
            </button>
          </div>
        </div>
      )
    }
  }

  // Render children or Outlet for nested routes
  return children ? <>{children}</> : <Outlet />
}

/**
 * Higher-order component version of AuthGuard
 */
export function withAuth<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  requiredRoles?: string[]
) {
  return function AuthenticatedComponent(props: P) {
    return (
      <AuthGuard requiredRoles={requiredRoles}>
        <WrappedComponent {...props} />
      </AuthGuard>
    )
  }
}
